import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { FraudPreventionService, CLEARANCE_STATUS, TRUST_TIERS } from './FraudPreventionService';

const API_BASE = 'https://klorah-fast-server-my8l.onrender.com/api';

/**
 * PayoutService manages the delayed payout lifecycle:
 *   Payment captured → Pending Clearance → Risk evaluation → Release or Hold
 */
export class PayoutService {
  constructor() {
    this.firestore = getFirestore();
    this.fraudService = new FraudPreventionService();
  }

  // ── Payment Capture Hook ──────────────────────────────────────────────

  /**
   * Called after a payment is successfully captured.
   * Creates payout clearance, evaluates risk, and checks for fraud flags.
   *
   * @param {string} userId     - Creator's user ID (connected account owner)
   * @param {string} invoiceId  - The paid invoice ID
   * @param {number} amount     - Payment amount in dollars
   * @returns {Object} { clearance, riskResult, fraudFlags }
   */
  async onPaymentCaptured(userId, invoiceId, amount) {
    // 1. Update the risk profile stats
    await this.fraudService.updateRiskProfileAfterPayment(userId, amount);

    // 2. Evaluate risk for this payment
    const riskResult = await this.fraudService.evaluatePaymentRisk(userId, {
      invoiceId,
      amount,
    });

    // 3. Check for fraud flags
    const fraudFlags = await this.fraudService.checkAndCreateFraudFlags(userId, {
      invoiceId,
      amount,
    });

    // 4. Create payout clearance (with delay based on trust tier)
    const clearance = await this.fraudService.createPayoutClearance(
      userId,
      invoiceId,
      amount
    );

    // 5. Evaluate if trust tier should be upgraded
    await this.fraudService.evaluateTrustTier(userId);

    // 6. Store payout eligibility timestamp on the invoice
    const invoiceRef = doc(this.firestore, 'invoices', invoiceId);
    const eligibleAt = clearance.eligibleAt;
    await updateDoc(invoiceRef, {
      payoutClearanceId: clearance.id,
      payoutStatus: clearance.status,
      payoutEligibleAt: eligibleAt,
      payoutDelayDays: clearance.payoutDelayDays,
      riskScore: riskResult.score,
      riskLevel: riskResult.level,
    });

    return { clearance, riskResult, fraudFlags };
  }

  // ── Payout Eligibility Check ──────────────────────────────────────────

  /**
   * Check which clearances are now eligible for payout release.
   * Should be called periodically or via a scheduled function.
   */
  async processEligiblePayouts() {
    const now = new Date();

    const q = query(
      collection(this.firestore, 'payoutClearances'),
      where('status', '==', CLEARANCE_STATUS.PENDING),
      orderBy('eligibleAt', 'asc')
    );

    const snap = await getDocs(q);
    const released = [];

    for (const docSnap of snap.docs) {
      const clearance = { id: docSnap.id, ...docSnap.data() };
      const eligibleAt = clearance.eligibleAt?.toDate
        ? clearance.eligibleAt.toDate()
        : new Date(clearance.eligibleAt);

      if (eligibleAt <= now) {
        // Re-check risk before releasing
        const profile = await this.fraudService.getAccountRiskProfile(clearance.userId);

        if (profile.accountFrozen) {
          continue; // Skip frozen accounts
        }

        // Release the clearance
        await this.fraudService.releaseClearance(clearance.id);

        // Update invoice payout status
        if (clearance.invoiceId) {
          const invoiceRef = doc(this.firestore, 'invoices', clearance.invoiceId);
          await updateDoc(invoiceRef, {
            payoutStatus: CLEARANCE_STATUS.CLEARED,
            payoutReleasedAt: serverTimestamp(),
          });
        }

        released.push(clearance);
      }
    }

    return released;
  }

  // ── Payout Status for Creator ─────────────────────────────────────────

  /**
   * Get a human-readable payout status for an invoice (creator-facing).
   */
  async getInvoicePayoutStatus(invoiceId) {
    const invoiceRef = doc(this.firestore, 'invoices', invoiceId);
    const invoiceSnap = await getDoc(invoiceRef);

    if (!invoiceSnap.exists()) {
      return null;
    }

    const invoice = invoiceSnap.data();

    if (invoice.status !== 'paid') {
      return {
        status: 'unpaid',
        message: 'Invoice has not been paid yet.',
        payoutEligible: false,
      };
    }

    if (!invoice.payoutClearanceId) {
      return {
        status: 'processing',
        message: 'Payment is being processed.',
        payoutEligible: false,
      };
    }

    const clearanceRef = doc(this.firestore, 'payoutClearances', invoice.payoutClearanceId);
    const clearanceSnap = await getDoc(clearanceRef);

    if (!clearanceSnap.exists()) {
      return {
        status: 'processing',
        message: 'Payout clearance record not found.',
        payoutEligible: false,
      };
    }

    const clearance = clearanceSnap.data();

    switch (clearance.status) {
      case CLEARANCE_STATUS.PENDING: {
        const eligibleDate = clearance.eligibleAt?.toDate
          ? clearance.eligibleAt.toDate()
          : new Date(clearance.eligibleAt);
        const daysRemaining = Math.max(
          0,
          Math.ceil((eligibleDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
        );

        return {
          status: 'pending_clearance',
          message: `Funds will be released in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} once cleared.`,
          eligibleDate,
          daysRemaining,
          payoutAmount: clearance.payoutAmount,
          reserveAmount: clearance.reserveAmount,
          payoutEligible: false,
        };
      }

      case CLEARANCE_STATUS.UNDER_REVIEW:
        return {
          status: 'under_review',
          message: 'This payment is under manual review. Funds will be released once approved.',
          payoutAmount: clearance.payoutAmount,
          reserveAmount: clearance.reserveAmount,
          payoutEligible: false,
        };

      case CLEARANCE_STATUS.CLEARED:
        return {
          status: 'cleared',
          message: 'Funds have been released for payout.',
          payoutAmount: clearance.payoutAmount,
          releasedAt: clearance.releasedAt,
          payoutEligible: true,
        };

      case CLEARANCE_STATUS.FROZEN:
        return {
          status: 'frozen',
          message: 'Payout has been temporarily held for review. Contact support for details.',
          frozenReason: clearance.frozenReason,
          payoutEligible: false,
        };

      case CLEARANCE_STATUS.CANCELLED:
        return {
          status: 'cancelled',
          message: 'Payout was cancelled due to a refund or dispute.',
          payoutEligible: false,
        };

      default:
        return {
          status: 'unknown',
          message: 'Unable to determine payout status.',
          payoutEligible: false,
        };
    }
  }

  // ── Dispute-Triggered Payout Cancellation ─────────────────────────────

  /**
   * Handle a dispute by cancelling any pending payout for the invoice.
   */
  async onDisputeCreated(userId, invoiceId, disputeData) {
    await this.fraudService.handleDisputeCreated(userId, {
      invoiceId,
      reason: disputeData.reason || 'Dispute filed',
    });

    // Update invoice status
    if (invoiceId) {
      const invoiceRef = doc(this.firestore, 'invoices', invoiceId);
      await updateDoc(invoiceRef, {
        payoutStatus: CLEARANCE_STATUS.FROZEN,
        disputeStatus: 'active',
        disputeReason: disputeData.reason || 'Unknown',
        disputeCreatedAt: serverTimestamp(),
      });
    }
  }

  // ── Refund During Delay ───────────────────────────────────────────────

  /**
   * Process a refund while funds are still in pending clearance.
   * Since funds haven't been paid out yet, this is straightforward.
   */
  async onRefundDuringDelay(userId, invoiceId) {
    // Find and cancel the clearance
    const q = query(
      collection(this.firestore, 'payoutClearances'),
      where('invoiceId', '==', invoiceId),
      where('status', 'in', [CLEARANCE_STATUS.PENDING, CLEARANCE_STATUS.UNDER_REVIEW])
    );
    const snap = await getDocs(q);

    for (const d of snap.docs) {
      await this.fraudService.cancelClearance(d.id, 'Refund issued during clearance period');
    }

    // Update invoice
    const invoiceRef = doc(this.firestore, 'invoices', invoiceId);
    await updateDoc(invoiceRef, {
      payoutStatus: CLEARANCE_STATUS.CANCELLED,
      refundedDuringClearance: true,
      refundedAt: serverTimestamp(),
    });
  }

  // ── Admin: Manual Payout Release ──────────────────────────────────────

  /**
   * Manually approve and release a payout clearance (admin action).
   */
  async adminReleasePayout(clearanceId, adminUserId) {
    const ref = doc(this.firestore, 'payoutClearances', clearanceId);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      throw new Error('Clearance not found');
    }

    const clearance = snap.data();

    await updateDoc(ref, {
      status: CLEARANCE_STATUS.CLEARED,
      releasedAt: serverTimestamp(),
      manuallyReleasedBy: adminUserId,
    });

    // Update invoice
    if (clearance.invoiceId) {
      const invoiceRef = doc(this.firestore, 'invoices', clearance.invoiceId);
      await updateDoc(invoiceRef, {
        payoutStatus: CLEARANCE_STATUS.CLEARED,
        payoutReleasedAt: serverTimestamp(),
      });
    }

    return { success: true, clearanceId };
  }

  // ── Balance Overview ──────────────────────────────────────────────────

  /**
   * Get a comprehensive balance overview including held / pending / cleared amounts.
   */
  async getBalanceOverview(userId, stripeAccountId) {
    // Get Stripe balance
    let stripeBalance = { available: 0, pending: 0 };
    if (stripeAccountId) {
      try {
        const response = await fetch(`${API_BASE}/balance/${stripeAccountId}`);
        if (response.ok) {
          const data = await response.json();
          stripeBalance = {
            available: Number(data.available?.[0]?.amount || 0) / 100,
            pending: Number(data.pending?.[0]?.amount || 0) / 100,
          };
        }
      } catch (err) {
        console.error('Error fetching Stripe balance:', err);
      }
    }

    // Get payout summary from fraud prevention system
    const payoutSummary = await this.fraudService.getPayoutSummary(userId);

    // Get risk profile
    const riskProfile = await this.fraudService.getAccountRiskProfile(userId);
    const tierConfig = Object.values(TRUST_TIERS).find(t => t.id === riskProfile.trustTier)
      || TRUST_TIERS.TIER_0;

    return {
      stripe: stripeBalance,
      payout: payoutSummary,
      riskProfile: {
        trustTier: riskProfile.trustTier,
        tierName: tierConfig.name,
        riskScore: riskProfile.riskScore,
        riskLevel: riskProfile.riskLevel,
        accountFrozen: riskProfile.accountFrozen,
        payoutDelayDays: tierConfig.payoutDelayDays,
      },
    };
  }
}

export default PayoutService;
